import { Link } from 'react-router-dom';
import { useI18n, useLocalized } from '../lib/i18n';
import { formatWhen } from '../lib/format';
import { useMyWork, useSetWork } from '../lib/queries';
import { Alert, Button, Card, EmptyState, Pill, Skeleton } from '../components/ui';
import { PhoneSetup } from '../components/PhoneSetup';
import type { MyJob } from '../lib/types';

/**
 * What this technician has to do, and nothing else.
 *
 * Opened on a phone with wet hands in a bay, so it is one column of big
 * targets: the car, what is wanted, and one button that moves the job on.
 * Everything a supervisor would want to see lives on the board instead.
 */
function Job({ job }: { job: MyJob }) {
  const { t } = useI18n();
  const localized = useLocalized();
  const setWork = useSetWork();

  const car = [job.vehicleMake, job.vehicleModel].filter(Boolean).join(' ');
  const running = job.workStatus === 'IN_PROGRESS';
  const late = job.promisedAt !== null && new Date(job.promisedAt) < new Date();

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-lg font-bold leading-tight text-ink">
              {car || t('floor.noVehicle')}
            </span>
            {job.vehiclePlate && (
              <span className="tabular rounded-md border border-line px-1.5 py-0.5 text-xs text-ink-dim">
                {job.vehiclePlate}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-dim">{localized(job.name)}</p>
          {job.quantity > 1 && (
            <p className="tabular mt-0.5 text-xs text-ink-faint">× {job.quantity}</p>
          )}
        </div>
        {running && <Pill tone="warning">{t('work.running')}</Pill>}
      </div>

      {job.promisedAt && (
        <p className={`tabular mt-2 text-xs ${late ? 'text-danger' : 'text-ink-faint'}`}>
          {t('floor.promisedAt', { time: formatWhen(job.promisedAt) })}
        </p>
      )}

      {job.notes && (
        <p className="mt-2 border-l-2 border-brand/40 pl-3 text-xs leading-relaxed text-ink-dim">
          {job.notes}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        {!running ? (
          <Button
            size="lg"
            className="flex-1"
            loading={setWork.isPending}
            onClick={() => setWork.mutate({ itemId: job.itemId, status: 'IN_PROGRESS' })}
          >
            {t('work.start')}
          </Button>
        ) : (
          <>
            <Button
              size="lg"
              className="flex-1"
              loading={setWork.isPending}
              onClick={() => setWork.mutate({ itemId: job.itemId, status: 'DONE' })}
            >
              {t('work.finish')}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => setWork.mutate({ itemId: job.itemId, status: 'PENDING' })}
            >
              {t('work.stop')}
            </Button>
          </>
        )}
      </div>

      <Link
        to={`/floor?car=${job.orderId}`}
        className="tabular mt-2 block text-center text-[0.7rem] text-ink-faint transition hover:text-ink-dim"
      >
        {job.reference}
      </Link>
    </Card>
  );
}

/** One line, so a technician can be told about a car without opening anything. */
export function MyWorkPage() {
  const { t } = useI18n();
  const { data, isPending, error } = useMyWork();

  const jobs = data?.items ?? [];
  const running = jobs.filter((j) => j.workStatus === 'IN_PROGRESS');
  const waiting = jobs.filter((j) => j.workStatus !== 'IN_PROGRESS');

  return (
    <div className="mx-auto max-w-lg pb-16">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
          {t('work.title')}
        </h1>
      </header>

      {error && <div className="mb-4"><Alert>{t('app.apiUnreachable')}</Alert></div>}

      {isPending ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-44" />)}</div>
      ) : jobs.length === 0 ? (
        <EmptyState title={t('work.empty')} />
      ) : (
        <div className="space-y-3">
          {/* Started jobs first: the one in your hands is the one you came to close. */}
          {[...running, ...waiting].map((job) => <Job key={job.itemId} job={job} />)}
        </div>
      )}

      <div className="mt-6"><PhoneSetup /></div>
    </div>
  );
}
